(() => {
  if (window.yjContentCarouselReady) return;
  window.yjContentCarouselReady = true;
  const instances = new Map();
  function mount(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('[data-yj-track]');
    const controls = root.querySelector('.yj-carousel-controls');
    if (!track || !controls) return;
    const cards = Array.from(track.children);
    if (!cards.length) return;
    const prev = controls.querySelector('[data-yj-prev]');
    const next = controls.querySelector('[data-yj-next]');
    const status = controls.querySelector('.yj-carousel-status');
    const toggle = controls.querySelector('[data-yj-toggle]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const events = new AbortController();
    const options = {signal: events.signal};
    let timer;
    let frame = 0;
    let lastTime = null;
    let lastStatusTime = 0;
    let position = track.scrollLeft;
    let direction = 1;
    let holdUntil = 0;
    let inView = false;
    let hovered = root.matches(':hover') && matchMedia('(hover: hover)').matches;
    let touching = false;
    let userPaused = false;
    // Keep the original cards and crawlable links; no duplicated slides for looping.
    if (toggle) root.dataset.yjAutoplay = 'true';
    function canRun() {
      return toggle && !userPaused && !hovered && !touching && !motion.matches && inView && !document.hidden && track.scrollWidth > track.clientWidth + 4;
    }
    function tick(now) {
      frame = 0;
      if (!canRun()) {sync();return;}
      const elapsed = lastTime === null ? 0 : Math.min((now - lastTime) / 1000, .1);
      lastTime = now;
      if (now >= holdUntil) {
        const maximum = track.scrollWidth - track.clientWidth;
        // Floating-point accumulation keeps 20px/s movement smooth at high refresh rates.
        position = Math.max(0, Math.min(maximum, position + direction * elapsed * 20));
        track.scrollLeft = position;
        if ((direction > 0 && position >= maximum) || (direction < 0 && position <= 0)) {
          direction *= -1;
          holdUntil = now + 3000;
        }
      }
      if (now - lastStatusTime >= 300) {update();lastStatusTime = now;}
      frame = requestAnimationFrame(tick);
    }
    function sync() {
      if (!toggle) return;
      toggle.hidden = motion.matches;
      const label = userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('title', label);
      toggle.querySelector('[data-yj-pause]').hidden = userPaused;
      toggle.querySelector('[data-yj-play]').hidden = !userPaused;
      status.setAttribute('aria-live', userPaused || motion.matches ? 'polite' : 'off');
      const running = canRun();
      root.dataset.yjAutoplayState = running ? 'running' : 'paused';
      if (running && !frame) {
        position = track.scrollLeft;
        lastTime = null;
        holdUntil = Math.max(holdUntil, performance.now() + 1600);
        frame = requestAnimationFrame(tick);
      } else if (!running && frame) {
        cancelAnimationFrame(frame);
        frame = 0;
        lastTime = null;
      }
    }
    function pauseByUser() {userPaused = true;sync();}
    function update() {
      const bounds = track.getBoundingClientRect();
      const visible = cards.map((card, i) => {
        const r = card.getBoundingClientRect();
        return Math.min(r.right, bounds.right) - Math.max(r.left, bounds.left) >= r.width * .5 ? i + 1 : null;
      }).filter(Boolean);
      controls.hidden = track.scrollWidth <= track.clientWidth + 4;
      prev.setAttribute('aria-disabled', String(track.scrollLeft <= 4));
      next.setAttribute('aria-disabled', String(track.scrollLeft >= track.scrollWidth - track.clientWidth - 4));
      if (visible.length) status.textContent = (visible[0] === visible.at(-1) ? visible[0] : `${visible[0]}-${visible.at(-1)}`) + ` / ${cards.length}`;
    }
    function move(direction) {
      pauseByUser();
      const button = direction < 0 ? prev : next;
      if (button.getAttribute('aria-disabled') === 'true') return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 14;
      const step = cards[0].getBoundingClientRect().width + gap;
      const current = Math.round(track.scrollLeft / step);
      const target = Math.max(0, Math.min(cards.length - 1, current + direction));
      track.scrollTo({left: target * step, behavior: motion.matches ? 'auto' : 'smooth'});
    }
    prev.addEventListener('click', () => move(-1), options);
    next.addEventListener('click', () => move(1), options);
    track.addEventListener('scroll', () => {if(frame)return;clearTimeout(timer);timer=setTimeout(update,100);}, {...options, passive:true});
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {event.preventDefault();move(event.key === 'ArrowLeft' ? -1 : 1);}
      if (event.key === 'Home' || event.key === 'End') {event.preventDefault();pauseByUser();track.scrollTo({left:event.key === 'Home' ? 0 : track.scrollWidth,behavior:'auto'});}
    }, options);
    root.addEventListener('pointerenter', event => {if(event.pointerType==='mouse'){hovered=true;sync();}}, options);
    root.addEventListener('pointerleave', event => {if(event.pointerType==='mouse'){hovered=false;sync();}}, options);
    root.addEventListener('pointerdown', event => {if(event.pointerType!=='mouse'){touching=true;sync();}}, options);
    function releaseTouch() {if(touching){touching=false;holdUntil=performance.now()+8000;sync();}}
    document.addEventListener('pointerup', releaseTouch, options);
    document.addEventListener('pointercancel', releaseTouch, options);
    root.addEventListener('focusin', event => {if(event.target!==toggle)pauseByUser();}, options);
    track.addEventListener('wheel', event => {if(Math.abs(event.deltaX)>0)pauseByUser();}, {...options,passive:true});
    toggle?.addEventListener('click', () => {userPaused=!userPaused;sync();}, options);
    document.addEventListener('visibilitychange', sync, options);
    motion.addEventListener('change', sync, options);
    const visibility = new IntersectionObserver(entries => {inView=entries[0].isIntersecting&&entries[0].intersectionRatio>=.25;sync();}, {threshold:[0,.25]});
    visibility.observe(track);
    const resize = new ResizeObserver(() => {update();sync();});
    resize.observe(track);
    controls.hidden = false;
    update();
    sync();
    instances.set(root, () => {events.abort();resize.disconnect();visibility.disconnect();clearTimeout(timer);cancelAnimationFrame(frame);});
  }
  function scan(scope) {
    if (scope.matches?.('[data-yj-carousel]')) mount(scope);
    scope.querySelectorAll('[data-yj-carousel]').forEach(mount);
  }
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target.contains(root)) {dispose();instances.delete(root);}
  });
})();
